import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";
import { useStandingNotice } from "../lib/useStandingNotice";
import { CopyButton } from "./DetailFields";
import { ErrorAlert } from "./ErrorAlert";
import { SettingRow } from "./SettingRow";
import { ShortDate } from "./ShortDate";

const TWITCH_CONSOLE_URL = "https://dev.twitch.tv/console/apps/create";

/**
 * The environment's own Twitch application (`twitchApps.*`): every Twitch feature of its live
 * breaks runs through it, and Twitch's consent screen names it, so it is the tenant's and never
 * the platform's. The status, the client id and secret (the secret write-only: the field is
 * always empty and the server never sends it back), the redirect URIs to register, one per store
 * hostname, and the steps to make the app. Admins save and remove; everyone else reads. A saved
 * secret that no longer decrypts under the platform's key (the key changed) turns every Twitch
 * feature off until the app is saved again, which the card's badge and a standing notice say.
 */
export function TwitchAppCard({ canEdit }: { canEdit: boolean }) {
  const trpc = useTRPC();
  const twitch = useQuery(trpc.twitchApps.get.queryOptions());

  const app = twitch.data?.app ?? null;
  // With no platform key at all the card already says Twitch isn't available here.
  const unreadable = !!twitch.data?.platformKeyConfigured && app !== null && !app.readable;
  useStandingNotice(unreadable, {
    id: "twitch-app-unreadable",
    message: canEdit
      ? "Your Twitch app's saved secret can no longer be read, so live breaks run without Twitch. Save the app again with its client secret."
      : "Your Twitch app's saved secret can no longer be read, so live breaks run without Twitch until an admin saves the app again.",
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Twitch
          {twitch.data &&
            (unreadable ? (
              <Badge variant="destructive">Save again</Badge>
            ) : app ? (
              <Badge>Set up</Badge>
            ) : (
              <Badge variant="outline">Not set up</Badge>
            ))}
        </CardTitle>
        <CardDescription>
          Your store's own Twitch application. Shoppers link their Twitch account through it, a live
          break shows its host's Twitch channel, and a shopper subscribed to that channel buys in
          with Priority. Twitch shows the app's name when a shopper connects, so it is yours alone.
          Without one, live breaks run with no Twitch features.
        </CardDescription>
        {canEdit && app && (
          <CardAction>
            <RemoveTwitchAppButton />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="grid gap-6">
        {twitch.error ? (
          <ErrorAlert message={twitch.error.message} />
        ) : !twitch.data ? (
          <Skeleton className="h-48" />
        ) : (
          <>
            {!twitch.data.platformKeyConfigured && (
              <p className="text-sm text-muted-foreground">
                Twitch isn't available on this platform yet, so an app can't be saved. Your live
                breaks run without Twitch until it is.
              </p>
            )}
            {app && (
              <dl className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
                <dt className="text-muted-foreground">Client ID</dt>
                <dd className="font-mono break-all">{app.clientId}</dd>
                <dt className="text-muted-foreground">Verified with Twitch</dt>
                <dd>
                  <ShortDate value={app.verifiedAt} />
                </dd>
              </dl>
            )}
            <TwitchAppForm
              key={app?.updatedAt ?? "none"}
              savedClientId={app?.clientId ?? null}
              unreadable={unreadable}
              disabled={!canEdit || !twitch.data.platformKeyConfigured}
            />
            {!canEdit && (
              <p className="text-sm text-muted-foreground">
                Only admins can change the Twitch app.
              </p>
            )}
            <RedirectUris redirectUris={twitch.data.redirectUris} />
            <SetupSteps />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The client id and secret. Twitch checks the pair before anything is saved, so the secret is
 * always asked for: the saved one never comes back to fill the field.
 */
function TwitchAppForm({
  savedClientId,
  unreadable,
  disabled,
}: {
  savedClientId: string | null;
  /** The saved secret no longer decrypts: it has to be entered again. */
  unreadable: boolean;
  disabled: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState(savedClientId ?? "");
  const [clientSecret, setClientSecret] = useState("");
  const save = useMutation(
    trpc.twitchApps.set.mutationOptions({
      onSuccess: () => {
        setClientSecret("");
        void queryClient.invalidateQueries({ queryKey: trpc.twitchApps.pathKey() });
        toast.success("Twitch app saved. Twitch accepted the client ID and secret.");
      },
    }),
  );

  const trimmedId = clientId.trim();
  const replacesApp = savedClientId !== null && trimmedId !== "" && trimmedId !== savedClientId;
  const locked = disabled || save.isPending;
  const ready = trimmedId !== "" && clientSecret.trim() !== "";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate({ clientId: trimmedId, clientSecret });
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="grid divide-y">
        <SettingRow
          htmlFor="twitch-client-id"
          label="Client ID"
          description="From your app's page in the Twitch developer console."
        >
          <div className="grid max-w-md gap-2">
            <Input
              id="twitch-client-id"
              value={clientId}
              disabled={locked}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              onChange={(e) => setClientId(e.target.value)}
            />
            {replacesApp && (
              <p className="text-xs text-destructive">
                A new client ID is a different app: every shopper's Twitch link is disconnected, and
                they connect again through the new one.
              </p>
            )}
          </div>
        </SettingRow>
        <SettingRow
          htmlFor="twitch-client-secret"
          label="Client secret"
          description="Make a new secret on the same page. It is never shown again once saved."
        >
          <div className="grid max-w-md gap-2">
            <Input
              id="twitch-client-secret"
              type="password"
              value={clientSecret}
              disabled={locked}
              autoComplete="new-password"
              spellCheck={false}
              placeholder={savedClientId ? "••••••••••••" : undefined}
              onChange={(e) => setClientSecret(e.target.value)}
            />
            {unreadable ? (
              <p className="text-xs text-destructive">
                The saved secret can no longer be read. Enter it again, or a new one, and save.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {savedClientId
                  ? "A secret is set. Enter it again, or a new one, to save a change."
                  : "Saved encrypted, and checked with Twitch first."}
              </p>
            )}
          </div>
        </SettingRow>
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Button type="submit" disabled={!ready || save.isPending}>
            {save.isPending ? "Checking with Twitch…" : "Save"}
          </Button>
        </div>
      )}
    </form>
  );
}

/** Removes the app after a confirmation; every linked shopper is disconnected with it. */
function RemoveTwitchAppButton() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const remove = useMutation(
    trpc.twitchApps.remove.mutationOptions({
      onSuccess: () => {
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.twitchApps.pathKey() });
        toast.success("Twitch app removed.");
      },
    }),
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="outline" size="sm" />}>
        Remove
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove the Twitch app?</AlertDialogTitle>
          <AlertDialogDescription>
            Every shopper who linked their Twitch account is disconnected, and has to connect again
            once you add an app. Until then live breaks run with no Twitch features: no channels, no
            Priority and no Twitch buttons.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? "Removing…" : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface RedirectUri {
  hostname: string;
  redirectUri: string;
  status: string;
  isPrimary: boolean;
}

/** One redirect URI per store hostname: Twitch sends a shopper back only to a registered one. */
function RedirectUris({ redirectUris }: { redirectUris: RedirectUri[] }) {
  return (
    <div className="grid gap-2">
      <div className="grid gap-1">
        <span className="text-sm font-medium">Redirect URIs</span>
        <p className="text-sm text-muted-foreground">
          Add each of these as an OAuth Redirect URL in your Twitch app. Twitch sends a shopper back
          to the store they connected from, so every hostname of this store needs its own. Add a
          domain, and add its URI here too.
        </p>
      </div>
      {redirectUris.length === 0 ? (
        <p className="text-sm text-muted-foreground">This store has no hostname yet.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {redirectUris.map((uri) => (
            <li key={uri.hostname} className="flex items-center gap-2 px-3 py-2">
              <span className="min-w-0 flex-1 font-mono text-xs break-all">{uri.redirectUri}</span>
              {uri.isPrimary && <Badge variant="secondary">Primary</Badge>}
              {uri.status !== "active" && <Badge variant="outline">Not live yet</Badge>}
              <CopyButton text={uri.redirectUri} label="Copy redirect URI" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** How to make the app on Twitch's side. */
function SetupSteps() {
  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium">Setting up the app</span>
      <ol className="ml-5 grid list-decimal gap-1 text-sm text-muted-foreground">
        <li>
          In the{" "}
          <a
            href={TWITCH_CONSOLE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-foreground underline"
          >
            Twitch developer console
          </a>
          , register an application named after your store: shoppers see the name when they connect.
        </li>
        <li>Choose the category Website Integration, and the client type Confidential.</li>
        <li>Add every redirect URI above as an OAuth Redirect URL.</li>
        <li>Create the app, open it, copy its client ID, then make a new secret and copy it.</li>
        <li>Paste both here and save. Twitch checks them before anything is saved.</li>
      </ol>
    </div>
  );
}
